import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  CalendarDays,
  Check,
  ChevronLeft,
  IndianRupee,
  MapPin,
} from "lucide-react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { STATUS_BEGIN, STATUS_DONE, STATUS_FAIL } from "utils/constants/others";
import usePlanShipmentStore from "./usePlanShipmentStore";
import {
  formatInvoiceAmount,
  formatScheduleDate,
  formatSKUCount,
  formatWeight,
} from "./planShipmentUtils";
import {
  BackButton,
  ContinueButton,
  CtaBar,
  Divider,
  DividerLabel,
  DividerLine,
  DividerSubtitle,
  ErrorMessage,
  Header,
  HeaderTitle,
  InputControl,
  InputField,
  InputGrid,
  InputLabel,
  InputRow,
  InputSuffix,
  MetadataGrid,
  MetadataIcon,
  MetadataItem,
  MetadataValue,
  POCard,
  POCardInfo,
  POCardList,
  POCardTop,
  POCheckbox,
  PONumber,
  POSubtitle,
  PageFrame,
  PageShell,
  ScrollArea,
  SectionTitle,
} from "./styles.selectPOsToSchedule";

const DEFAULT_MIN_T_PLUS_EDD = 10;

const POCardContent = ({
  po,
  isSelected,
  onToggle,
  netWeight,
  numSkus,
  onInputChange,
  weightSuffix,
  skuSuffix,
  maxWeight,
  maxSkus,
  isSchedulable = true,
  minDays = DEFAULT_MIN_T_PLUS_EDD,
  disabledReason = null,
}) => {
  const id = Number(po.purchase_order_id);

  return (
    <POCard onClick={() => isSchedulable && onToggle(id)} $disabled={!isSchedulable}>
      <POCardTop>
        {isSchedulable ? (
          <POCheckbox
            type="button"
            $checked={isSelected}
            onClick={(e) => {
              e.stopPropagation();
              onToggle(id);
            }}
            aria-label={isSelected ? "Deselect this PO" : "Select this PO"}
          >
            {isSelected ? <Check aria-hidden="true" /> : null}
          </POCheckbox>
        ) : null}
        <POCardInfo>
          <PONumber>{po.purchase_order_number || "—"}</PONumber>
          <POSubtitle>
            {formatSKUCount(po.total_sku_count)}
            {" • "}
            {formatWeight(po.total_weight_in_kg)}
          </POSubtitle>
        </POCardInfo>
      </POCardTop>

      <MetadataGrid $disabled={!isSchedulable}>
        <MetadataItem>
          <MetadataIcon>
            <CalendarDays aria-hidden="true" />
          </MetadataIcon>
          <MetadataValue>
            {formatScheduleDate(po.expected_delivery_date)}
          </MetadataValue>
        </MetadataItem>
        <MetadataItem>
          <MetadataIcon>
            <MapPin aria-hidden="true" />
          </MetadataIcon>
          <MetadataValue>
            {(() => {
              const raw = po.warehouse_code || "";
              if (!raw) return "—";
              const commaIndex = raw.indexOf(",");
              if (commaIndex === -1) return raw;
              const code = raw.slice(0, commaIndex).trim();
              const city = raw.slice(commaIndex + 1).trim();
              return (
                <>
                  {code}
                  {city ? <><br />{city}</> : null}
                </>
              );
            })()}
          </MetadataValue>
        </MetadataItem>
        <MetadataItem>
          <MetadataIcon>
            <IndianRupee aria-hidden="true" />
          </MetadataIcon>
          <MetadataValue>{formatInvoiceAmount(po.po_amount)}</MetadataValue>
        </MetadataItem>
      </MetadataGrid>

      {isSchedulable ? (
        <>
          {isSelected ? (
            <InputGrid onClick={(e) => e.stopPropagation()}>
              <InputField>
                <InputLabel>Net weight</InputLabel>
                <InputRow>
                  <InputControl
                    type="number"
                    inputMode="decimal"
                    value={netWeight}
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (raw === "") {
                        onInputChange(id, "netWeight", "");
                        return;
                      }
                      const parsed = Number(raw);
                      if (!Number.isFinite(parsed)) return;
                      const clamped =
                        Number.isFinite(maxWeight) && parsed > maxWeight
                          ? String(maxWeight)
                          : raw;
                      onInputChange(id, "netWeight", clamped);
                    }}
                  />
                  <InputSuffix>{weightSuffix}</InputSuffix>
                </InputRow>
              </InputField>
              <InputField>
                <InputLabel>No. of SKUs</InputLabel>
                <InputRow>
                  <InputControl
                    type="number"
                    inputMode="numeric"
                    step="1"
                    min="0"
                    value={numSkus}
                    onKeyDown={(e) => {
                      if (e.key === "." || e.key === ",") e.preventDefault();
                    }}
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (raw === "") {
                        onInputChange(id, "numSkus", "");
                        return;
                      }
                      const intVal = Math.floor(Math.abs(Number(raw)));
                      if (!Number.isFinite(intVal)) return;
                      const clamped =
                        Number.isFinite(maxSkus) && intVal > maxSkus
                          ? String(maxSkus)
                          : String(intVal);
                      onInputChange(id, "numSkus", clamped);
                    }}
                  />
                  <InputSuffix>{skuSuffix}</InputSuffix>
                </InputRow>
              </InputField>
            </InputGrid>
          ) : null}
        </>
      ) : null}

      {!isSchedulable && (
        <div style={{ padding: "10px 0 2px", textAlign: "center", color: "#293142", fontSize: "12px", fontFamily: '"OurLexend-Regular", Helvetica, sans-serif' }}>
          {disabledReason
            ? disabledReason
            : `Scheduling will be enabled after ${getSchedulingEnabledDate(po.expected_delivery_date, minDays)}`}
        </div>
      )}
    </POCard>
  );
};

const isDateWithinRange = (date, referenceDate, daysOffset = 1) => {
  if (!date || !referenceDate) return false;
  const d1 = new Date(date);
  const d2 = new Date(referenceDate);
  d1.setHours(0, 0, 0, 0);
  d2.setHours(0, 0, 0, 0);
  const diffTime = Math.abs(d1 - d2);
  const diffDays = diffTime / (1000 * 60 * 60 * 24);
  return diffDays <= daysOffset;
};

const canSchedulePO = (eddDate, minDays = DEFAULT_MIN_T_PLUS_EDD) => {
  if (!eddDate) return true;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const edd = new Date(eddDate);
  edd.setHours(0, 0, 0, 0);
  const daysUntilEDD = (edd - today) / (1000 * 60 * 60 * 24);
  return daysUntilEDD <= minDays;
};

const getSchedulingEnabledDate = (eddDate, minDays = DEFAULT_MIN_T_PLUS_EDD) => {
  if (!eddDate) return null;
  const edd = new Date(eddDate);
  const enableDate = new Date(edd);
  enableDate.setDate(enableDate.getDate() - minDays);
  return enableDate.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "2-digit",
  });
};

const SelectPOsToSchedule = () => {
  const { poId, scheduleGroupId: editScheduleGroupIdParam } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isEditMode = !!editScheduleGroupIdParam;
  const editScheduleGroupId = Number(editScheduleGroupIdParam);

  const {
    getPlanShipmentPurchaseOrders,
    getPlanShipmentPurchaseOrdersStatus,
    planShipmentPurchaseOrders,
    planShipmentPurchaseOrdersHasMore,
    loadMorePlanShipmentPurchaseOrders,
    getPlanShipmentPurchaseOrdersError,
    getScheduleGroupPurchaseOrders,
    getScheduleGroupPurchaseOrdersStatus,
    scheduleGroupPurchaseOrders,
    getScheduleGroupPurchaseOrdersError,
    planShipmentAsnConfig,
    getPlanShipmentHelpSections,
  } = usePlanShipmentStore();

  const minDays = (() => {
    const raw = Number(planShipmentAsnConfig?.min_t_plus_edd_for_schedule_creation);
    return Number.isFinite(raw) && raw > 0 ? raw : DEFAULT_MIN_T_PLUS_EDD;
  })();

  const minWeightPercent = (() => {
    const raw = Number(planShipmentAsnConfig?.per_schedule_total_remaining_min_percent);
    return Number.isFinite(raw) && raw > 0 ? raw : 0;
  })();

  const vendorId = window.localStorage.getItem("outlet_id");
  const primaryPoId = Number(poId);
  const primaryPoDataFromState = location.state?.primaryPoData || null;
  const scheduleGroupDeliveryDate = location.state?.scheduleGroupDeliveryDate || null;
  const additionalSentinelRef = useRef(null);

  // Scope the PO list to EDD±1 so only compatible POs are fetched.
  const makeEddRangeParams = (eddStr) => {
    if (!eddStr) return {};
    const base = new Date(eddStr);
    if (isNaN(base.getTime())) return {};
    const pad = (n) => String(n).padStart(2, "0");
    const fmt = (d) =>
      `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const minus1 = new Date(base);
    minus1.setDate(base.getDate() - 1);
    const plus1 = new Date(base);
    plus1.setDate(base.getDate() + 1);
    return {
      start_expected_delivery_date: fmt(minus1),
      end_expected_delivery_date: fmt(plus1),
    };
  };
  const eddDateParams = isEditMode
    ? makeEddRangeParams(scheduleGroupDeliveryDate)
    : makeEddRangeParams(primaryPoDataFromState?.expected_delivery_date);

  useEffect(() => {
    getPlanShipmentPurchaseOrders({ vendor_id: vendorId, ...eddDateParams });
    if (isEditMode) {
      getScheduleGroupPurchaseOrders({
        vendor_id: vendorId,
        schedule_group_id: editScheduleGroupId,
        page_size: 50,
        page_number: 1,
      });
    }
    if (!planShipmentAsnConfig && vendorId) {
      getPlanShipmentHelpSections({ seller_outlet_id: vendorId });
    }
  }, []);

  // Normalize scheduled POs to share the same field names as planShipmentPurchaseOrders
  const normalizedScheduledPOs = useMemo(
    () =>
      scheduleGroupPurchaseOrders.map((po) => ({
        purchase_order_id: po.purchase_order_id,
        purchase_order_number: po.purchase_order_number,
        total_sku_count: po.total_sku_count,
        total_weight_in_kg: po.total_order_weight_in_kg,
        expected_delivery_date: po.expected_delivery_date,
        warehouse_code: po.warehouse_code,
        po_amount: po.total_price,
        remaining_weight_to_schedule: po.remaining_weight_to_schedule,
        created_by_phone_number: po.created_by_phone_number,
        zone_name: po.zone_name,
        _scheduled_weight: po.scheduled_weight_in_kg,
        _scheduled_sku_count: po.scheduled_sku_count,
      })),
    [scheduleGroupPurchaseOrders]
  );

  const scheduledPoIdSet = useMemo(
    () => new Set(normalizedScheduledPOs.map((po) => Number(po.purchase_order_id))),
    [normalizedScheduledPOs]
  );

  const primaryPO = isEditMode
    ? null
    : (planShipmentPurchaseOrders.find(
        (p) => Number(p.purchase_order_id) === primaryPoId
      ) || primaryPoDataFromState);

  const referencePO = isEditMode
    ? normalizedScheduledPOs[0]
    : primaryPO;

  const additionalPOs = useMemo(() => {
    let filtered = isEditMode
      ? planShipmentPurchaseOrders.filter(
          (p) => !scheduledPoIdSet.has(Number(p.purchase_order_id))
        )
      : planShipmentPurchaseOrders.filter(
          (p) => Number(p.purchase_order_id) !== primaryPoId
        );

    if (referencePO) {
      filtered = filtered.filter(
        (po) =>
          po.warehouse_code === referencePO.warehouse_code &&
          isDateWithinRange(
            po.expected_delivery_date,
            referencePO.expected_delivery_date,
            1
          )
      );
    }

    return filtered;
  }, [isEditMode, scheduledPoIdSet, primaryPoId, planShipmentPurchaseOrders, referencePO]);

  const [selectedPoIds, setSelectedPoIds] = useState(
    () => new Set(isEditMode ? [] : [primaryPoId])
  );
  const [poInputs, setPoInputs] = useState(() =>
    isEditMode ? {} : { [primaryPoId]: { netWeight: "", numSkus: "" } }
  );
  const [weightError, setWeightError] = useState("");

  // In edit mode: pre-populate once schedule group POs load
  const editInitialized = useRef(false);
  useEffect(() => {
    if (!isEditMode || editInitialized.current) return;
    if (getScheduleGroupPurchaseOrdersStatus !== STATUS_DONE) return;

    editInitialized.current = true;
    setSelectedPoIds(
      new Set(normalizedScheduledPOs.map((po) => Number(po.purchase_order_id)))
    );
    setPoInputs((prev) => {
      const next = { ...prev };
      normalizedScheduledPOs.forEach((po) => {
        const id = Number(po.purchase_order_id);
        next[id] = {
          netWeight: String(po._scheduled_weight ?? ""),
          numSkus: String(po._scheduled_sku_count ?? ""),
        };
      });
      return next;
    });
  }, [isEditMode, getScheduleGroupPurchaseOrdersStatus, normalizedScheduledPOs]);

  const editEddFetched = useRef(false);
  useEffect(() => {
    if (!isEditMode || editEddFetched.current) return;
    // If we already have EDD params from route state, no fallback needed.
    if (scheduleGroupDeliveryDate) return;
    if (getScheduleGroupPurchaseOrdersStatus !== STATUS_DONE) return;
    const firstPO = normalizedScheduledPOs[0];
    if (!firstPO) return;
    const params = makeEddRangeParams(firstPO.expected_delivery_date);
    if (!params.start_expected_delivery_date) return;
    editEddFetched.current = true;
    getPlanShipmentPurchaseOrders({ vendor_id: vendorId, ...params });
  }, [isEditMode, scheduleGroupDeliveryDate, getScheduleGroupPurchaseOrdersStatus, normalizedScheduledPOs, vendorId, getPlanShipmentPurchaseOrders]);



  // Keep poInputs in sync as additional POs load (without overwriting existing)
  useEffect(() => {
    if (!planShipmentPurchaseOrders.length) return;
    setPoInputs((prev) => {
      const next = { ...prev };
      planShipmentPurchaseOrders.forEach((po) => {
        const id = Number(po.purchase_order_id);
        if (!next[id]) {
          next[id] = { netWeight: "", numSkus: "" };
        }
      });
      return next;
    });
  }, [planShipmentPurchaseOrders]);

  // Infinite scroll for additional POs
  useEffect(() => {
    const sentinel = additionalSentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) loadMorePlanShipmentPurchaseOrders(); },
      { threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadMorePlanShipmentPurchaseOrders, planShipmentPurchaseOrdersHasMore, planShipmentPurchaseOrders.length]);

  const togglePO = (id) => {
    const numericId = Number(id);
    setSelectedPoIds((prev) => {
      const next = new Set(prev);
      if (next.has(numericId)) {
        next.delete(numericId);
      } else {
        next.add(numericId);
      }
      return next;
    });
  };

  const updateInput = (id, field, value) => {
    const numericId = Number(id);
    setPoInputs((prev) => ({
      ...prev,
      [numericId]: { ...prev[numericId], [field]: value },
    }));
  };

  const allPOsById = useMemo(() => {
    const map = {};
    const allPOs = isEditMode
      ? [...normalizedScheduledPOs, ...additionalPOs]
      : planShipmentPurchaseOrders;
    allPOs.forEach((po) => {
      map[Number(po.purchase_order_id)] = po;
    });
    return map;
  }, [isEditMode, normalizedScheduledPOs, additionalPOs, planShipmentPurchaseOrders]);

  const canContinue =
    selectedPoIds.size > 0 &&
    Array.from(selectedPoIds).every((id) => {
      const inputs = poInputs[id];
      const weight = String(inputs?.netWeight ?? "").trim();
      const skus = String(inputs?.numSkus ?? "").trim();
      if (!weight || Number(weight) <= 0 || !skus || Number(skus) <= 0) return false;
      const po = allPOsById[id];
      const maxWeight = Number(po?.remaining_weight_to_schedule);
      const maxSkus = Number(po?.total_sku_count);
      if (Number.isFinite(maxWeight) && Number(weight) > maxWeight) return false;
      if (Number.isFinite(maxSkus) && Number(skus) > maxSkus) return false;
      return true;
    });

  const handleContinue = () => {
    if (!canContinue) return;

    if (minWeightPercent > 0) {
      for (const id of selectedPoIds) {
        const po = allPOsById[id];
        const totalWeight = Number(po?.total_weight_in_kg);
        const enteredWeight = Number(poInputs[id]?.netWeight);
        if (Number.isFinite(totalWeight) && totalWeight > 0) {
          const minRequired = (minWeightPercent / 100) * totalWeight;
          if (enteredWeight < minRequired) {
            setWeightError(
              `Entered weight for PO ${po.purchase_order_number || id} must be at least ${minWeightPercent}% of the PO net weight (≥ ${minRequired.toFixed(2)} kg).`
            );
            return;
          }
        }
      }
    }
    setWeightError("");

    const allPOs = isEditMode
      ? [...normalizedScheduledPOs, ...additionalPOs]
      : [
          ...(primaryPoDataFromState ? [primaryPoDataFromState] : []),
          ...planShipmentPurchaseOrders,
        ];
    const poMeta = {};
    Array.from(selectedPoIds).forEach((id) => {
      const po = allPOs.find((p) => Number(p.purchase_order_id) === id);
      if (po) {
        poMeta[id] = {
          phone: po.created_by_phone_number || "",
          warehouseName: po.warehouse_code || "",
          amount: po.po_amount || 0,
          purchaseOrderNumber: po.purchase_order_number || "",
          edd: po.expected_delivery_date || "",
        };
      }
    });
    navigate("/plan-shipment/purchase-orders/slot-selection", {
      state: {
        selectedPoIds: Array.from(selectedPoIds),
        poInputs,
        poMeta,
        isEdit: isEditMode,
        scheduleGroupId: isEditMode ? editScheduleGroupId : undefined,
        scheduleGroupDeliveryDate: isEditMode ? (scheduleGroupDeliveryDate || null) : undefined,
      },
    });
  };

  const isLoadingCreate =
    (getPlanShipmentPurchaseOrdersStatus === STATUS_BEGIN ||
      !getPlanShipmentPurchaseOrdersStatus) &&
    planShipmentPurchaseOrders.length === 0;
  const isLoadingEdit =
    isEditMode &&
    (getScheduleGroupPurchaseOrdersStatus === STATUS_BEGIN ||
      !getScheduleGroupPurchaseOrdersStatus);
  const isLoading = isLoadingCreate || isLoadingEdit;

  const isError =
    getPlanShipmentPurchaseOrdersStatus === STATUS_FAIL ||
    (isEditMode && getScheduleGroupPurchaseOrdersStatus === STATUS_FAIL);
  const errorMessage =
    getPlanShipmentPurchaseOrdersError ||
    getScheduleGroupPurchaseOrdersError ||
    "";

  const getWeightSuffix = (po) =>
    `/ ${formatWeight(po.remaining_weight_to_schedule)}`;
  const getSkuSuffix = (po) =>
    `/ ${po.total_sku_count != null ? po.total_sku_count : "—"} SKUs`;

  return (
    <PageShell>
      <PageFrame>
        <Header>
          <BackButton
            type="button"
            onClick={() => navigate(-1)}
            aria-label="Go back"
          >
            <ChevronLeft aria-hidden="true" />
          </BackButton>
          <HeaderTitle>Schedule POs</HeaderTitle>
        </Header>

        <ScrollArea>
          <SectionTitle>Select POs to schedule</SectionTitle>

          {isError ? (
            <div
              style={{ textAlign: "center", padding: "32px 0", color: "#7d8599" }}
            >
              <p style={{ margin: "0 0 12px" }}>{errorMessage}</p>
              <button
                type="button"
                onClick={() => {
                  getPlanShipmentPurchaseOrders({ vendor_id: vendorId, ...eddDateParams });
                  if (isEditMode) {
                    getScheduleGroupPurchaseOrders({
                      vendor_id: vendorId,
                      schedule_group_id: editScheduleGroupId,
                      page_size: 50,
                      page_number: 1,
                    });
                  }
                }}
                style={{
                  color: "#ef4f5f",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "15px",
                }}
              >
                Try again
              </button>
            </div>
          ) : null}

          {!isError ? (
            <POCardList>
              {/* Edit mode: show pre-checked scheduled POs first */}
              {isEditMode && !isLoading
                ? normalizedScheduledPOs.map((po) => {
                    const id = Number(po.purchase_order_id);
                    return (
                      <POCardContent
                        key={id}
                        po={po}
                        isSelected={selectedPoIds.has(id)}
                        onToggle={togglePO}
                        netWeight={poInputs[id]?.netWeight ?? ""}
                        numSkus={poInputs[id]?.numSkus ?? ""}
                        onInputChange={updateInput}
                        weightSuffix={getWeightSuffix(po)}
                        skuSuffix={getSkuSuffix(po)}
                        maxWeight={Number(po.remaining_weight_to_schedule)}
                        maxSkus={Number(po.total_sku_count)}
                        isSchedulable={canSchedulePO(po.expected_delivery_date, minDays)}
                        minDays={minDays}
                       />
                     );
                   })
                 : null}

              {/* Create mode: show primary PO first */}
              {!isEditMode && !isLoading && primaryPO ? (
                <POCardContent
                  po={primaryPO}
                  isSelected={selectedPoIds.has(primaryPoId)}
                  onToggle={togglePO}
                  netWeight={poInputs[primaryPoId]?.netWeight ?? ""}
                  numSkus={poInputs[primaryPoId]?.numSkus ?? ""}
                  onInputChange={updateInput}
                  weightSuffix={getWeightSuffix(primaryPO)}
                  skuSuffix={getSkuSuffix(primaryPO)}
                  maxWeight={Number(primaryPO.remaining_weight_to_schedule)}
                  maxSkus={Number(primaryPO.total_sku_count)}
                  isSchedulable={canSchedulePO(primaryPO.expected_delivery_date, minDays)}
                  minDays={minDays}
                />
              ) : null}

              {/* Additional POs section */}
              {!isLoading && additionalPOs.length > 0 ? (
                <>
                  <Divider>
                    <DividerLine>
                      <DividerLabel>Add POs for this schedule</DividerLabel>
                    </DividerLine>
                    <DividerSubtitle>
                      Based on similar delivery date &amp; location
                    </DividerSubtitle>
                  </Divider>

                  {additionalPOs.map((po) => {
                    const id = Number(po.purchase_order_id);
                    const refZoneName = referencePO?.zone_name || "";
                    const isZoneMismatch =
                      !!refZoneName &&
                      !!po.zone_name &&
                      po.zone_name !== refZoneName;
                    if (isZoneMismatch) return null;
                    const schedulable = canSchedulePO(po.expected_delivery_date, minDays);
                    return (
                      <POCardContent
                          key={id}
                          po={po}
                          isSelected={selectedPoIds.has(id)}
                          onToggle={togglePO}
                          netWeight={poInputs[id]?.netWeight ?? ""}
                          numSkus={poInputs[id]?.numSkus ?? ""}
                          onInputChange={updateInput}
                          weightSuffix={getWeightSuffix(po)}
                          skuSuffix={getSkuSuffix(po)}
                          maxWeight={Number(po.remaining_weight_to_schedule)}
                          maxSkus={Number(po.total_sku_count)}
                          isSchedulable={schedulable}
                          minDays={minDays}
                        />
                    );
                  })}
                  {planShipmentPurchaseOrdersHasMore ? (
                    <div ref={additionalSentinelRef} style={{ height: 1 }} />
                  ) : null}
                </>
              ) : null}
            </POCardList>
          ) : null}
        </ScrollArea>

        <CtaBar>
          {weightError ? <ErrorMessage>{weightError}</ErrorMessage> : null}
          <ContinueButton
            type="button"
            disabled={!canContinue || isLoading}
            onClick={handleContinue}
          >
            Continue
          </ContinueButton>
        </CtaBar>
      </PageFrame>
    </PageShell>
  );
};

export default SelectPOsToSchedule;
